<template>
    <v-dialog v-model="show" max-width="520" overlay-color="#000" overlay-opacity="0.8" :persistent="loading" :fullscreen="phoneOnly" scrollable>
        <v-card class="px-lg-2 pb-1">
            <v-card-title class="mb-0 pe-10">{{ booking ? 'تم تأكيد حجزك' : 'حجز مقعد' }}</v-card-title>
            <v-card-subtitle class="pb-2">{{ tour.title }} - {{ tour.governorate }}</v-card-subtitle>

            <v-card-text class="strong-text--text pb-0 pt-3">
                <template v-if="booking">
                    <v-alert type="success" outlined text dense class="details-text mb-4">
                        <div>احضر قبل الموعد بـ 15 دقيقة.</div>
                        <div>اعرض رمز QR عند باب القاعة.</div>
                        <div>تجد تذكرتك دائماً في «تذاكري».</div>
                    </v-alert>
                    <tour-ticket :booking="booking" />
                </template>

                <template v-else>
                    <v-alert v-if="error" type="error" dense text class="details-text mb-4">{{ error }}</v-alert>
                    <div class="rounded bordered pa-3 mb-4">
                        <div class="details-text text--text d-flex align-center mb-1">
                            <v-icon small class="me-2">mdi-calendar-blank-outline</v-icon>
                            {{ tourDate(tour.starts_at_timestamp) }} · {{ tourTime(tour.starts_at_timestamp) }} - {{ tourTime(tour.ends_at_timestamp) }}
                        </div>
                        <div class="details-text text--text d-flex align-center">
                            <v-icon small class="me-2">mdi-map-marker-outline</v-icon>
                            {{ tour.address }}
                        </div>
                    </div>

                    <!-- Same price block as package-dialog.vue -->
                    <div class="mb-4 rounded bordered pa-3 d-flex justify-space-between align-center">
                        <div>
                            <v-icon color="primary" size="20" class="me-2">mdi-cash</v-icon>
                            <span class="details-text strong-text--text">سعر الحجز</span>
                        </div>
                        <b v-if="isFree" class="secondary--text">مجانية</b>
                        <div v-else class="strong-text--text" style="font-size: 1.15rem">{{ moneyFormat(tour.price, true) }}</div>
                    </div>

                    <v-form v-if="tour.fields.length" ref="form" @submit.prevent>
                        <div class="details-text text--text mb-3">أدخل البيانات المطلوبة للحجز:</div>
                        <v-text-field v-for="(label, i) in tour.fields" :key="i" v-model="answers[i]" :label="label" :disabled="loading" :rules="[v => !!(v && v.trim()) || `أدخل ${label}`]" outlined dense maxlength="255"></v-text-field>
                        <div class="details-text muted--text mt-n2 mb-4">راجع بياناتك جيداً، ستظهر في تذكرتك ولا يمكن تعديلها.</div>
                    </v-form>

                    <template v-if="!isFree">
                        <div class="details-text text--text mb-3">اختر طريقة الدفع:</div>
                        <v-list class="choose-payment-method pa-0">
                            <v-list-item v-for="method in paymentMethods" :key="method.value" :class="{'v-list-item--active': paymentMethod === method.value, [`${method.color.split(' ')[0]}--text`]: paymentMethod === method.value}" class="bordered rounded py-1 mb-2" v-ripple="phoneOnly" @click="paymentMethod = method.value">
                                <v-list-item-icon class="me-3">
                                    <v-icon :color="paymentMethod === method.value ? method.color : 'text'">{{ paymentMethod === method.value ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon>
                                    <v-icon size="28" :color="method.color" class="ms-1">{{ method.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title class="strong-text--text" style="font-size: 15px">{{ method.title }}</v-list-item-title>
                                    <v-list-item-subtitle class="details-text" style="line-height: 1.4!important">{{ method.subtitle }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>

                        <v-expand-transition mode="out-in">
                            <!-- Wallet and pay-card panels copied from package-dialog.vue -->
                            <div v-if="paymentMethod === 'wallet'" key="wallet" class="mt-2">
                                <div class="mb-2 rounded bordered pa-3 d-flex justify-space-between">
                                    <div>
                                        <v-icon color="primary" size="20" class="me-2">mdi-wallet</v-icon>
                                        <span class="details-text strong-text--text">رصيد المحفظة</span>
                                    </div>
                                    <div>
                                        <v-chip small label outlined :color="noBalance ? 'error' : 'primary'" dark class="ms-3 px-2" style="height: 22px; font-weight: 600;">{{moneyFormat(user.balance, true)}}</v-chip>
                                    </div>
                                </div>
                                <div v-if="noBalance" class="text-center pa-3 rounded bordered mb-3">
                                    <div class="details-text muted--text mb-2">رصيد محفظتك {{ moneyFormat(user.balance, true) }} غير كافي، لإتمام الحجز ستحتاج لشحن المبلغ التالي:</div>
                                    <div class="strong-text--text" style="font-size: 1.5rem">{{moneyFormat(tour.price - user.balance)}} <span class="medium-text muted--text">جنيه</span></div>
                                </div>
                                <v-btn v-if="noBalance" class="mb-2" color="primary" depressed block :to="{name: 'wallet', query: {recharge: user.balance ? tour.price - user.balance : tour.price}}">
                                    <v-icon left>mdi-wallet-plus</v-icon>
                                    <span>شحن رصيد المحفظة</span>
                                </v-btn>
                                <div v-if="noBalance" class="details-text muted--text text-center mb-2">بعد الشحن ارجع إلى صفحة الجولات لإكمال الحجز</div>
                                <div v-if="!noBalance" class="text-center pa-3 rounded bordered">
                                    <div class="details-text muted--text mb-2">سيتم خصم المبلغ التالي من رصيد محفظتك:</div>
                                    <div class="strong-text--text" style="font-size: 1.5rem">{{moneyFormat(tour.price)}} <span class="medium-text muted--text">جنيه</span></div>
                                </div>
                            </div>
                            <div v-else-if="paymentMethod === 'paycard'" key="paycard" class="mt-2">
                                <div class="mb-3 details-text text--text">
                                    أدخل الكود الموجود على بطاقة الدفع:
                                </div>
                                <v-text-field v-model="code" :disabled="loading" :error-messages="codeError" prepend-inner-icon="mdi-credit-card-outline" class="code-input" color="secondary" outlined :hide-details="!codeError" placeholder="كود بطاقة الدفع" @input="codeError = ''"></v-text-field>
                            </div>
                        </v-expand-transition>
                    </template>
                </template>
            </v-card-text>

            <v-card-actions :class="{'px-6 mt-4': pcOnly, 'px-4 mt-4': phoneOnly}">
                <v-btn icon absolute top left color="muted" :disabled="loading" @click="show = false">
                    <v-icon>{{ phoneOnly ? 'mdi-arrow-left' : 'mdi-close' }}</v-icon>
                </v-btn>
                <v-btn v-if="booking" color="primary" large block depressed class="mx-0" @click="show = false; $emit('view-ticket')">عرض تذكرتي</v-btn>
                <v-btn v-else color="success" large block depressed class="mx-0" :loading="loading" @click="submit">
                    {{ isFree ? 'تأكيد الحجز مجاناً' : 'تأكيد الحجز' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import TourTicket from '@/components/custom/tour-ticket'
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    components: {TourTicket},
    props: {
        value: {},
        tour: {type: Object, required: true},
    },
    data(){
        return {
            show: !!this.value,
            loading: false,
            code: '',
            paymentMethod: '',
            answers: [],
            codeError: '',
            error: '',
            booking: null,
        }
    },
    computed: {
        ...GlobalComputed,
        isFree(){
            return !(this.tour.price > 0);
        },
        paymentMethods(){
            const methods = [{value: 'paycard', title: 'بطاقة دفع', subtitle: 'الدفع المباشر بكود بطاقة الدفع', icon: 'mdi-credit-card-outline', color: 'success darken-1'}];
            if (this.appData.enable_wallet_system){
                methods.unshift({value: 'wallet', title: 'محفظة الحساب', subtitle: 'الدفع برصيد محفظة الحساب الخاص بك', icon: 'mdi-wallet-outline', color: 'primary'});
            }
            return methods;
        },
        noBalance(){
            return (this.user.balance || 0) < this.tour.price;
        },
    },
    watch: {
        value(show){
            this.show = show;
        },
        show(show){
            if (show){
                this.reset();
            }else{
                this.$emit('input', false);
            }
        },
        code(code){
            if (code && code.search(' ') !== -1){
                this.code = code.replace(/ /g, '');
            }
        },
    },
    methods: {
        ...GlobalMethods,
        reset(){
            this.booking = null;
            this.code = '';
            this.codeError = '';
            this.error = '';
            this.answers = this.tour.fields.map(() => '');
            // Wallet first when it covers the price; otherwise the code, so the student isn't stuck on a dead end.
            this.paymentMethod = this.appData.enable_wallet_system && !this.noBalance ? 'wallet' : 'paycard';
        },
        submit(){
            const fieldsOk = !this.$refs.form || this.$refs.form.validate();
            const canSubmit = fieldsOk && !this.loading && (this.isFree || (this.paymentMethod === 'paycard' ? !!this.code : !this.noBalance));
            if (!canSubmit){
                if (fieldsOk && this.paymentMethod === 'paycard' && !this.code){
                    this.codeError = 'أدخل كود بطاقة الدفع';
                } else if (fieldsOk && this.paymentMethod === 'wallet' && this.noBalance){
                    this.showMsg('رصيد المحفظة غير كافي لإتمام الحجز', 'error');
                }
                return;
            }
            this.loading = true;
            this.error = '';
            const data = {fields: this.answers};
            if (!this.isFree){
                data.pay_by = this.paymentMethod;
                if (this.paymentMethod === 'paycard'){
                    data.code = this.code;
                }
            }
            this.$store.dispatch('bookTour', {id: this.tour.id, data}).then(response => {
                this.booking = response.data.data;
                this.$emit('booked', this.booking);
            }).catch(err => {
                const res = err && err.response ? err.response.data : null;
                if (res && res.errors && res.errors.code){
                    this.codeError = res.errors.code[0]; // shown on the field, not in a toast that disappears
                } else if (err && err.response && err.response.status === 404){
                    this.error = 'لم تعد هذه الجولة متاحة';
                } else {
                    if (res && res.errors && res.errors.pay_by){
                        this.$store.dispatch('loadWalletData', {}); // the real balance may be lower than the one on screen
                    }
                    const errors = res && res.errors ? Object.values(res.errors) : [];
                    // Inside the dialog, not a toast that disappears behind it (e.g. "اكتمل العدد" on a stale page).
                    this.error = (errors[0] && errors[0][0]) || (res && res.message) || 'حدثت مشكلة أثناء تنفيذ طلبك';
                }
                this.$emit('failed');
            }).finally(() => {
                this.loading = false;
            });
        },
    },
}
</script>
